import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { listEventPayouts, recordDistribution, type InvestorPayout } from '../../lib/spv';
import { ApiError } from '../../lib/apiClient';
import { formatMoney, formatDateShort } from '../../lib/format';
import './AdminPages.css';

/**
 * Record that the SPV distributed to an investor.
 *
 * THIS IS AN ATTESTATION, NOT AN INTEGRATION. Voleto does not send this money and cannot
 * see it move: net proceeds left for the SPV's own account at settlement, and the SPV
 * pays its investors from there. An admin who has seen the SPV's confirmation records
 * what was sent and when.
 *
 * Which is why the amount is typed rather than prefilled and locked. The computed figure
 * is what settlement said was owed; an SPV may round, withhold, or pay in tranches, and
 * the investor's ledger should show what actually arrived. The computed amount is shown
 * alongside so a difference is obvious rather than silent.
 *
 * Nothing here is inferred. Until a row is recorded, the investor's "Distributed to date"
 * stays at zero, because that is the truth.
 */

function PayoutRow({ payout, onRecorded }: { payout: InvestorPayout; onRecorded: () => void }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState((payout.netPayoutCents / 100).toFixed(2));
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cents = Math.round(Number(amount) * 100);
  const differs = Number.isFinite(cents) && cents !== payout.netPayoutCents;

  async function submit() {
    if (!Number.isFinite(cents) || cents <= 0) { setError('Enter the amount that was sent.'); return; }
    setBusy(true);
    setError(null);
    try {
      await recordDistribution(payout.id, {
        amountCents: cents,
        distributedAt: date || null,
        reference: reference.trim() || null,
      });
      onRecorded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record the distribution.');
      setBusy(false);
    }
  }

  return (
    <article className="net-row">
      <div className="net-row__main">
        <span className="net-row__email mono">{payout.contractNumber}</span>
        <span className="net-row__name">{payout.investor.email}</span>
        <div className="net-row__badges">
          {payout.isDistributed ? (
            <span className="badge badge--ok">Distributed</span>
          ) : (
            <span className="badge badge--warn">Awaiting the SPV</span>
          )}
        </div>
        <span className="opp-reg__hint">
          Owed {formatMoney(payout.netPayoutCents)} after the 10% performance fee
          {payout.isDistributed && payout.distributedAmountCents != null && (
            <>
              {' · sent '}
              <strong>{formatMoney(payout.distributedAmountCents)}</strong>
              {` on ${formatDateShort(payout.distributedAt)}`}
              {payout.confirmedBy ? ` · confirmed by ${payout.confirmedBy}` : ''}
            </>
          )}
        </span>
        {payout.distributionReference && (
          <span className="opp-reg__hint mono">Ref {payout.distributionReference}</span>
        )}
        {error && <p className="adm-error" role="alert">{error}</p>}
      </div>

      <div className="net-row__actions">
        {!payout.isDistributed && (
          <button type="button" className="button button--primary adm-btn-sm"
            onClick={() => setOpen((v) => !v)}>
            {open ? 'Cancel' : 'Record distribution'}
          </button>
        )}
      </div>

      {open && !payout.isDistributed && (
        <div className="net-row__panel">
          <p className="opp-reg__hint">
            Record what the SPV actually sent. Voleto does not make this payment, so this
            is your confirmation that it happened, and it is what the investor will see.
          </p>
          <div className="wiz-grid">
            <label className="field">
              <span className="field__label">Amount sent *</span>
              <input className="field__input mono" type="number" step="0.01" min="0"
                value={amount} onChange={(e) => setAmount(e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Date the SPV sent it *</span>
              <input className="field__input" type="date" value={date}
                onChange={(e) => setDate(e.target.value)} />
            </label>
            <label className="field wiz-field--wide">
              <span className="field__label">Reference (optional)</span>
              <input className="field__input" type="text" value={reference}
                placeholder="Wire confirmation, check number"
                onChange={(e) => setReference(e.target.value)} />
            </label>
          </div>
          {differs && (
            <p className="opp-reg__hint">
              This differs from the {formatMoney(payout.netPayoutCents)} settlement
              calculated. That is allowed, and the investor will see the amount you enter.
            </p>
          )}
          <button type="button" className="button button--primary" disabled={busy}
            onClick={() => void submit()}>
            {busy ? 'Recording…' : 'Confirm distribution'}
          </button>
        </div>
      )}
    </article>
  );
}

export function AdminDistributions() {
  const [params, setParams] = useSearchParams();
  const eventId = params.get('eventId') ?? '';
  const [input, setInput] = useState(eventId);
  const [rows, setRows] = useState<InvestorPayout[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!eventId) { setRows(null); return; }
    listEventPayouts(eventId)
      .then((r) => { setRows(r); setError(null); })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load payouts.'));
  }, [eventId]);

  useEffect(load, [load]);

  const pending = (rows ?? []).filter((r) => !r.isDistributed);

  return (
    <div className="adm-page" data-surface="invest">
      <header className="adm-header">
        <div>
          <h1 className="adm-title">Investor distributions</h1>
          <p className="adm-lede">
            What each investor is owed for a settled event, and what the SPV has actually
            sent them. Voleto does not make these payments, so nothing here is assumed:
            an investor's ledger shows zero distributed until it is recorded on this screen.
          </p>
        </div>
      </header>

      <section className="net-panel">
        <label className="field wiz-field--wide">
          <span className="field__label">Event ID</span>
          <input className="field__input mono" type="text" value={input}
            placeholder="Paste the event id"
            onChange={(e) => setInput(e.target.value)} />
        </label>
        <button type="button" className="button button--primary"
          onClick={() => setParams(input.trim() ? { eventId: input.trim() } : {})}>
          Load payouts
        </button>
      </section>

      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {rows != null && rows.length === 0 && (
        <p className="adm-status adm-muted">
          No investor payouts for this event. They are created when the event settles.
        </p>
      )}

      {pending.length > 0 && (
        <p className="adm-status adm-muted">
          <strong>{pending.length}</strong>
          {pending.length === 1 ? ' investor is' : ' investors are'} still awaiting the SPV.
        </p>
      )}

      <div className="adm-list">
        {(rows ?? []).map((p) => <PayoutRow key={p.id} payout={p} onRecorded={load} />)}
      </div>
    </div>
  );
}
